import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import type { UserRole } from '@/lib/types';

/**
 * Administering an account, as opposed to listing one.
 *
 * The list has always been read-only here, so a role was changed by calling the
 * API by hand. Every one of these invalidates the list, because the change
 * shows up in the row the administrator is looking at.
 */

interface CreateUser {
  email: string;
  name: string;
  password: string;
  role: UserRole;
  organizationId: string;
}

function useInvalidateUsers() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['users'] });
}

export function useCreateUser() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (input: CreateUser) =>
      apiFetch<{ id: string; email: string }>('/v1/admin/users', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: invalidate,
  });
}

export function useUpdateUser() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: string;
      role?: UserRole;
      isActive?: boolean;
      name?: string;
      email?: string;
    }) => apiFetch(`/v1/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    onSuccess: invalidate,
  });
}

/** Deactivates the account and closes every session it holds. */
export function useDeactivateUser() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/v1/admin/users/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/** Signs the user out everywhere without touching the account itself. */
export function useRevokeSessions() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ revoked: number }>(`/v1/admin/users/${id}/revoke-sessions`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}
